import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserPlus } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "bg-[#1E222D] border-[#2A2E39] text-[#EAECEF] placeholder:text-[#5E6673] focus-visible:ring-[#F0B90B]/50";
const labelCls = "text-[11px] font-medium uppercase tracking-wider text-[#848E9C]";

export default function CreateClientDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", initial_deposit: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/clients", {
        name: form.name,
        email: form.email,
        password: form.password,
        phone: form.phone,
        initial_deposit: parseFloat(form.initial_deposit) || 0,
      });
      toast.success(`Client account created for ${form.name}`);
      setOpen(false);
      setForm({ name: "", email: "", password: "", phone: "", initial_deposit: "" });
      onCreated?.();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="create-client-account-btn"
          className="flex items-center gap-2 rounded-md bg-[#F0B90B] px-4 py-2 text-sm font-semibold text-black shadow-[0_0_12px_rgba(240,185,11,0.25)] transition-colors hover:bg-[#F8D12F]"
        >
          <UserPlus className="h-4 w-4" />
          New Client
        </button>
      </DialogTrigger>
      <DialogContent data-testid="create-client-modal" className="border-[#2A2E39] bg-[#131722] text-[#EAECEF] sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Create Client Account</DialogTitle>
          <DialogDescription className="text-sm text-[#848E9C]">
            Set login credentials and an opening balance for a new client.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className={labelCls}>Full Name</Label>
            <Input data-testid="client-name-input" value={form.name} onChange={set("name")} required placeholder="John Doe" className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className={labelCls}>Email</Label>
            <Input data-testid="client-email-input" type="email" value={form.email} onChange={set("email")} required placeholder="client@email.com" className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className={labelCls}>Phone Number (optional)</Label>
            <Input data-testid="client-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 98765 43210" className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className={labelCls}>Login Password</Label>
            <Input data-testid="client-password-input" type="text" value={form.password} onChange={set("password")} required minLength={6} placeholder="Min. 6 characters" className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className={labelCls}>Initial Deposit (USD)</Label>
            <Input data-testid="client-initial-deposit-input" type="number" step="0.01" min="0" value={form.initial_deposit} onChange={set("initial_deposit")} placeholder="0.00" className={`${inputCls} font-mono`} />
          </div>
          <button
            data-testid="submit-create-client-btn"
            type="submit"
            disabled={loading}
            className="w-full rounded-md bg-[#F0B90B] py-2.5 text-sm font-semibold text-black shadow-[0_0_12px_rgba(240,185,11,0.25)] transition-colors hover:bg-[#F8D12F] disabled:opacity-50"
          >
            {loading ? "Creating..." : "Create Account"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
